/**
 * 検索語に一致した箇所へ印（<span class="highlight">）を付けるユーティリティ（フロントエンド版）
 * バックエンド版: backend/adonis/app/Utils/NormalizeNameUtil.js / backend/adonis/app/Utils/UserKeywordSearchUtil.js
 * 区切り文字・氏名の正規化・照合のモードを変えるときは両方を同時に更新すること
 *
 * 検索の絞り込みは DB の LIKE（照合 utf8mb4_0900_ai_ci）が行う。LIKE は検索語の 1 文字と値の 1 文字を
 * 「字の骨格」（照合の第 1 レベル）で比べ、それが連続して成り立つ位置を探す。かなの種類・全角半角・大小・
 * 濁点の有無などは区別しないが、文字数がずれる比べ方はしない（「ｶﾞｷ」は「がき」に当たらない）。
 * 画面の印もこれと同じ形で探さないと「検索で出たのに印が無い」行が出るので、1 文字ずつ照合キーに変えて比べる。
 */

/** 区切り文字を除いてから照合する列（BE UserKeywordSearchUtil の NAME_COLUMNS と同じ） */
export const NAME_COLUMNS = ['username', 'kana'];

/** 区切りとして取り除く文字（BE NormalizeNameUtil の SEPARATOR_CODE_POINTS と同じ並び） */
export const SEPARATOR_CODE_POINTS = [
  0x0009, 0x000A, 0x000B, 0x000C, 0x000D,
  0x0020, 0x00A0, 0x1680,
  0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200A,
  0x2028, 0x2029, 0x202F, 0x205F, 0x3000, 0xFEFF,
  0x0027, 0xFF07,
  0x002D, 0xFE63, 0xFF0D,
  0x002E, 0x2024, 0xFE52, 0xFF0E,
];

const SEPARATORS = new Set(SEPARATOR_CODE_POINTS);
const SEPARATOR_REGEX = new RegExp(`[${SEPARATOR_CODE_POINTS
  .map((cp) => `\\u${cp.toString(16).toUpperCase().padStart(4, '0')}`)
  .join('')}]`, 'g');

const HIGHLIGHT_OPEN = '<span class="highlight">';
const HIGHLIGHT_CLOSE = '</span>';

// 照合で無視される文字（重みが空の文字）のキー。DB はこれらも 1 文字として位置を占め、無視される文字どうしは等しいとみなす。
// 制御文字は NFKD からは作られないので、印字できる文字のキーとは重ならない（U+0000 自身はこのキーになるが、DB でも無視される文字）
const IGNORABLE_KEY = '\u0000';
// 互換文字が NFKD で統合漢字 1 文字になるときのキーの接頭辞（下の foldChar 参照）
const IDEOGRAPH_VARIANT_PREFIX = '\u0001';

/*
 * NFKD の規則だけでは DB と同じ同値類にならない文字の手当て（キーは DB で等しい文字と同じものにする）。
 * 中身は tests/unit/fixtures/collation0900Primary.json（DB から機械生成）との全数検査で決めた。
 * その検査が落ちたら、表示される「足す／直す項目」をここへ反映する。
 */
// [キー, そのキーに寄せる文字]。コメントは同じ類の文字の例（先頭が NFKD の規則で既にそのキーになる文字）
const FOLD_EXCEPTION_GROUPS = [
  ['0', '\u24FF\u3007'], // 0 ⁰ ₀ ⓿ 〇
  ['1', '\u24F5\u2776\u2780\u278A\u3021'], // 1 ¹ ₁ ⓵ ❶ ➀ ➊ 〡
  ['2', '\u24F6\u2777\u2781\u278B\u3022'], // 2 ² ₂ ⓶ ❷ ➁ ➋ 〢
  ['3', '\u24F7\u2778\u2782\u278C\u3023'], // 3 ³ ₃ ⓷ ❸ ➂ ➌ 〣
  ['4', '\u24F8\u2779\u2783\u278D\u3024'], // 4 ⁴ ₄ ⓸ ❹ ➃ ➍ 〤
  ['5', '\u24F9\u277A\u2784\u278E\u3025'], // 5 ⁵ ₅ ⓹ ❺ ➄ ➎ 〥
  ['6', '\u2185\u24FA\u277B\u2785\u278F\u3026'], // 6 ⁶ ₆ ↅ ⓺ ❻ ➅ ➏ 〦
  ['7', '\u24FB\u277C\u2786\u2790\u3027'], // 7 ⁷ ₇ ⓻ ❼ ➆ ➐ 〧
  ['8', '\u24FC\u277D\u2787\u2791\u3028'], // 8 ⁸ ₈ ⓼ ❽ ➇ ➑ 〨
  ['9', '\u24FD\u277E\u2788\u2792\u3029'], // 9 ⁹ ₉ ⓽ ❾ ➈ ➒ 〩
  ['d', '\u00D0\u00F0\u0110\u0111'], // D d Ď Ð ð Đ đ
  ['h', '\u0126\u0127\u210F'], // H h Ĥ Ħ ħ ℏ
  ['l', '\u013F\u0140\u0141\u0142'], // L l Ĺ Ŀ ŀ Ł ł
  ['o', '\u00D8\u00F8\u01FE\u01FF'], // O o º Ø ø Ǿ ǿ
  ['\u0238', '\u33C8'], // ȸ ㏈
  ['a/s', '\u214D'], // ℁ ⅍
  ['10', '\u24FE\u277F\u2789\u2793\u3248'], // ⑩ ⓾ ❿ ➉ ➓ ㉈
  ['11', '\u24EB'], // ⑪ ⓫
  ['12', '\u24EC'], // ⑫ ⓬
  ['13', '\u24ED'], // ⑬ ⓭
  ['14', '\u24EE'], // ⑭ ⓮
  ['15', '\u24EF'], // ⑮ ⓯
  ['16', '\u24F0'], // ⑯ ⓰
  ['17', '\u24F1'], // ⑰ ⓱
  ['18', '\u24F2'], // ⑱ ⓲
  ['19', '\u24F3'], // ⑲ ⓳
  ['20', '\u24F4\u3249'], // ⑳ ⓴ ㉉
  ['\u2E80', '\u2F02'], // ⺀ ⼂
  ['\u2E81', '\u2F1A'], // ⺁ ⼚
  ['\u0001\u4E59', '\u2E84'], // ⼄ ㆚ ⺄
  ['\u2E86', '\u2F0C'], // ⺆ ⼌
  ['\u2E87', '\u2F0F'], // ⺇ ⼏
  ['\u2E88', '\u2F11'], // ⺈ ⼑
  ['\u2E8A', '\u2F18'], // ⺊ ⼘
  ['\u2E8B', '\u2F19'], // ⺋ ⼙
  ['\u2E8C', '\u2E8D\u2F29'], // ⺌ ⺍ ⼩
  ['\u2E8E', '\u2E90\u2F2A'], // ⺎ ⺐ ⼪
  ['\u2E8F', '\u2E91'], // ⺏ ⺑
  ['\u2E93', '\u2F33'], // ⺓ ⼳
  ['\u2E95', '\u2F39'], // ⺕ ⼹
  ['\u2E97', '\u2F3C'], // ⺗ ⼼
  ['\u0001\u65E5', '\u2E9C'], // ⽇ ㊐ ⺜
  ['\u0001\u6708', '\u2E9D'], // ⽉ ㊊ ⺝
  ['\u2EA4', '\u2EA5'], // ⺤ ⺥
  ['\u2EA7', '\u2F5C'], // ⺧ ⽜
  ['\u2EAA', '\u2F66'], // ⺪ ⽦
  ['\u2EAB', '\u2F6C'], // ⺫ ⽬
  ['\u2EAC', '\u2F70'], // ⺬ ⽰
  ['\u2EAE', '\u2F75'], // ⺮ ⽵
  ['\u2EB1', '\u2EB3\u2EB4'], // ⺱ ⺳ ⺴
  ['\u2EB2', '\u2EB5'], // ⺲ ⺵
  ['\u2EB6', '\u2EB7\u2F7A'], // ⺶ ⺷ ⽺
  ['\u2EBB', '\u2F80'], // ⺻ ⾀
  ['\u2EBC', '\u2F81'], // ⺼ ⾁
  ['\u2EBD', '\u2F85'], // ⺽ ⾅
  ['\u2EBE', '\u2EBF\u2EC0'], // ⺾ ⺿ ⻀
  ['\u2EC6', '\u2EC7\u2F93'], // ⻆ ⻇ ⾓
  ['\u2ECA', '\u2F9C'], // ⻊ ⾜
  ['\u2ECC', '\u2ECD\u2ECE'], // ⻌ ⻍ ⻎
  ['\u2ECF', '\u2FA2'], // ⻏ ⾢
  ['\u2ED1', '\u2FA7'], // ⻑ ⾧
  ['\u2ED5', '\u2FA9'], // ⻕ ⾩
  ['\u2ED7', '\u2FAC'], // ⻗ ⾬
  ['\u2EDD', '\u2FB7'], // ⻝ ⾷
  ['\u2EDE', '\u2EDF'], // ⻞ ⻟
  ['\u2EE1', '\u2FB8'], // ⻡ ⾸
  ['\u2EE3', '\u2FBB'], // ⻣ ⾻
  ['\u2EE4', '\u2FC1'], // ⻤ ⿁
  ['\u2EE7', '\u2FC4'], // ⻧ ⿄
  ['\u2EEB', '\u2FD1'], // ⻫ ⿑
  ['\u2EED', '\u2FD2'], // ⻭ ⿒
  ['\u2EEF', '\u2FD3'], // ⻯ ⿓
  ['\u2EF1', '\u2EF2\u2FD4'], // ⻱ ⻲ ⿔
  ['\u3031', '\u3032'], // 〱 〲
  ['\u3033', '\u3034'], // 〳 〴
  ['\u304F', '\u31F0'], // く ぐ ク ㇰ
  ['\u3057', '\u31F1'], // し じ シ ㇱ
  ['\u3059', '\u31F2'], // す ず ス ㇲ
  ['\u3068', '\u31F3'], // と ど ト ㇳ
  ['\u306C', '\u31F4'], // ぬ ヌ ㋦ ㇴ
  ['\u306F', '\u31F5'], // は ば ぱ ㇵ
  ['\u3072', '\u31F6'], // ひ び ぴ ㇶ
  ['\u3075', '\u31F7'], // ふ ぶ ぷ ㇷ
  ['\u3078', '\u31F8'], // へ べ ぺ ㇸ
  ['\u307B', '\u31F9'], // ほ ぼ ぽ ㇹ
  ['\u3080', '\u31FA'], // む ム ㋰ ㇺ
  ['\u3089', '\u31FB'], // ら ラ ㋶ ㇻ
  ['\u308A', '\u31FC'], // り リ ㋷ ㇼ
  ['\u308B', '\u31FD'], // る ル ㋸ ㇽ
  ['\u308C', '\u31FE'], // れ レ ㋹ ㇾ
  ['\u308D', '\u31FF'], // ろ ロ ㋺ ㇿ
  ['\u3105', '\u31A0'], // ㄅ ㆠ
  ['\u3106', '\u31B4'], // ㄆ ㆴ
  ['\u310A', '\u31B5'], // ㄊ ㆵ
  ['\u310D', '\u31A3'], // ㄍ ㆣ
  ['\u310E', '\u31B6'], // ㄎ ㆶ
  ['\u310F', '\u31B7'], // ㄏ ㆷ
  ['\u3110', '\u31A2'], // ㄐ ㆢ
  ['\u3117', '\u31A1'], // ㄗ ㆡ
  ['\u311A', '\u31A9'], // ㄚ ㆩ
  ['\u311B', '\u31A7'], // ㄛ ㆧ
  ['\u311E', '\u31AE'], // ㄞ ㆮ
  ['\u3120', '\u31AF'], // ㄠ ㆯ
  ['\u3127', '\u31AA\u31B3'], // ㄧ ㆪ ㆳ
  ['\u3128', '\u31A8\u31AB'], // ㄨ ㆨ ㆫ
  ['\u31A4', '\u31A5'], // ㆤ ㆥ
  ['\u324A', '\u325A'], // ㉊ ㉚
  ['\u324B', '\u32B5'], // ㉋ ㊵
  ['\u324C', '\u32BF'], // ㉌ ㊿
];
const FOLD_EXCEPTIONS = {};
FOLD_EXCEPTION_GROUPS.forEach(([key, chars]) => {
  Array.from(chars).forEach((ch) => { FOLD_EXCEPTIONS[ch] = key; });
});

const keyCache = new Map();

/**
 * 区切り文字だけを取り除く（BE NormalizeNameUtil.stripSeparators と同じ）
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function stripSeparators(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(SEPARATOR_REGEX, '');
}

/**
 * 氏名を比較用に正規化する（BE NormalizeNameUtil.normalizeName と同じ: NFKC → 小文字 → 区切り除去 → NFC）
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function normalizeName(value) {
  if (!value) return '';
  return stripSeparators(String(value).normalize('NFKC').toLowerCase()).normalize('NFC');
}

function computeKey(ch) {
  if (Object.prototype.hasOwnProperty.call(FOLD_EXCEPTIONS, ch)) return FOLD_EXCEPTIONS[ch];
  // 書式制御文字（ゼロ幅スペース・方向制御等）は照合で無視される
  if (/^\p{Cf}$/u.test(ch)) return IGNORABLE_KEY;
  const decomposed = ch.normalize('NFKD');
  // 互換文字（㊞・㊀ 等）が NFKD で統合漢字 1 文字になる場合、DB は互換文字どうしは等しいとみなすが、
  // 統合漢字そのもの（印・一）とは LIKE で当たらない。統合漢字とは別のキーにして、互換文字どうしだけを揃える
  if (decomposed !== ch && /^\p{Unified_Ideograph}$/u.test(decomposed)) return IDEOGRAPH_VARIANT_PREFIX + decomposed;
  const base = decomposed.replace(/\p{M}/gu, '');
  if (!base) return IGNORABLE_KEY;
  // 付加記号だけの字（゛ ¨ ￣ 等）は NFKD で「空白＋結合文字」になる。結合文字を除くと空白と同じキーになってしまうので、
  // 分解した形をそのままキーにする（DB でも空白とは別の文字）
  if (!base.trim() && ch.trim()) return decomposed;
  return Array.from(base.toLowerCase(), (c) => {
    const cp = c.codePointAt(0);
    // カタカナ（ァ〜ヶ）→ ひらがな
    const hiragana = cp >= 0x30A1 && cp <= 0x30F6 ? String.fromCodePoint(cp - 0x60) : c;
    // 小さいかな → 大きいかな
    return 'ぁぃぅぇぉっゃゅょゎゕゖ'.includes(hiragana)
      ? 'あいうえおつやゆよわかけ'['ぁぃぅぇぉっゃゅょゎゕゖ'.indexOf(hiragana)]
      : hiragana;
  }).join('');
}

/**
 * 1 文字（1 コード点）の照合キー。DB の LIKE が等しいとみなす文字どうしは同じキーになる
 * @param {string} ch 1 コード点の文字列
 * @returns {string}
 */
export function foldChar(ch) {
  let key = keyCache.get(ch);
  if (key === undefined) {
    key = computeKey(ch);
    keyCache.set(ch, key);
  }
  return key;
}

/** 文字列を 1 コード点ずつに分け、元の文字列での位置（UTF-16）を持たせる */
function toChars(text) {
  const chars = [];
  let offset = 0;
  Array.from(text).forEach((ch) => {
    chars.push({
      ch, index: chars.length, start: offset, end: offset + ch.length,
    });
    offset += ch.length;
  });
  return chars;
}

/**
 * chars の中で keyword のキー列と連続して一致する範囲を、左から重ならないように集める。
 * 範囲は元の文字列の文字の添字（from 以上 to 未満）で返す（chars が区切りを除いた列でも、元の列の添字になる）
 */
function findInChars(chars, keyword) {
  const keys = Array.from(keyword, foldChar);
  const ranges = [];
  if (!keys.length || keys.length > chars.length) return ranges;
  let i = 0;
  while (i + keys.length <= chars.length) {
    let j = 0;
    while (j < keys.length && foldChar(chars[i + j].ch) === keys[j]) j += 1;
    if (j === keys.length) {
      ranges.push({ from: chars[i].index, to: chars[i + keys.length - 1].index + 1 });
      i += keys.length;
    } else {
      i += 1;
    }
  }
  return ranges;
}

/**
 * text の中で keyword に一致する範囲を返す（BE の検索句と同じ照合）。
 *   - 氏名系の列（NAME_COLUMNS）: 値と語の両方から区切り文字を除いて、「区切りだけ除いた語」と「normalizeName した語」で探す。
 *     範囲は元の位置に戻すので、間の区切りも含む。語が区切りだけのときは区切りを除かずに探す
 *   - それ以外の列: 語をそのまま探す
 * % _ \ も他の文字と同じく文字として比べる（BE も文字として扱う）。
 *
 * @param {string|number|null|undefined} text 値
 * @param {string|number|null|undefined} keyword 検索語
 * @param {{ column?: string, firstOnly?: boolean }} [options] firstOnly: 最初の 1 か所だけ返す
 * @returns {Array<{ start: number, end: number }>} 元の文字列での UTF-16 位置（昇順・重なりはまとめる）
 */
export function findKeywordRanges(text, keyword, { column, firstOnly = false } = {}) {
  if ((typeof text !== 'string' && typeof text !== 'number') || (typeof keyword !== 'string' && typeof keyword !== 'number')) return [];
  const value = String(text);
  const rawKeyword = String(keyword);
  if (!value || !rawKeyword) return [];

  const chars = toChars(value);
  let found = [];
  const strippedKeyword = stripSeparators(rawKeyword);
  if (NAME_COLUMNS.includes(column) && strippedKeyword) {
    const kept = chars.filter(({ ch }) => !SEPARATORS.has(ch.codePointAt(0)));
    const keywords = [strippedKeyword];
    const normalizedKeyword = normalizeName(rawKeyword);
    if (normalizedKeyword && normalizedKeyword !== strippedKeyword) keywords.push(normalizedKeyword);
    keywords.forEach((word) => { found = found.concat(findInChars(kept, word)); });
  } else {
    found = findInChars(chars, rawKeyword);
  }
  if (!found.length) return [];

  // 表示だけの補正: 直後に続く無視される文字（半角の濁点 ﾞ 等）を範囲に含め、<span>ｶ</span>ﾞ と字を割らない。
  // 延ばした範囲が次の範囲に食い込みうるので、重なりをまとめる前に延ばす
  const extended = found.map(({ from, to }) => {
    let end = to;
    while (end < chars.length && foldChar(chars[end].ch) === IGNORABLE_KEY) end += 1;
    return { start: chars[from].start, end: chars[end - 1].end };
  });
  extended.sort((a, b) => a.start - b.start || a.end - b.end);
  const ranges = [];
  extended.forEach((range) => {
    const last = ranges[ranges.length - 1];
    if (last && range.start < last.end) {
      last.end = Math.max(last.end, range.end);
    } else {
      ranges.push(range);
    }
  });
  return firstOnly ? ranges.slice(0, 1) : ranges;
}

/**
 * 文字列全体を印で括る
 * @param {string} text
 * @returns {string}
 */
export function wrapHighlight(text) {
  return `${HIGHLIGHT_OPEN}${text}${HIGHLIGHT_CLOSE}`;
}

/**
 * text の中で keyword に一致した元の文字を印で括った文字列を返す。
 * HTML エスケープはしない（印を描く側が補間・v-html を使い分けている）。
 *
 * @param {string|number|null|undefined} text
 * @param {string|number|null|undefined} keyword
 * @param {{ column?: string, firstOnly?: boolean }} [options] findKeywordRanges と同じ
 * @returns {string|null} 一致が無ければ null
 */
export function highlightKeyword(text, keyword, options) {
  const ranges = findKeywordRanges(text, keyword, options);
  if (!ranges.length) return null;
  const value = String(text);
  let result = '';
  let cursor = 0;
  ranges.forEach(({ start, end }) => {
    result += value.slice(cursor, start) + wrapHighlight(value.slice(start, end));
    cursor = end;
  });
  return result + value.slice(cursor);
}
